import React from 'react';
import { Avatar as AvatarBase, styled } from '@mui/material';
import { getAvatarUrl } from '../../utils/avatarUrl';

export const AvatarWithInitialsFallBack = ({
  showProfilePhoto,
  profilePhoto,
  name,
  initials,
  InitialsFallback = InitialsAvatar,
}: any) => {
  // A URL built from the email can point at a picture that does not exist (S3 answers 403).
  // Remember which URL failed so a different one gets its own attempt.
  const [failedPhoto, setFailedPhoto] = React.useState<string | null>(null);

  if (showProfilePhoto && failedPhoto !== profilePhoto) {
    return (
      <StyledAvatar>
        <AvatarImage
          src={profilePhoto}
          alt={name}
          onError={() => setFailedPhoto(profilePhoto)}
        />
      </StyledAvatar>
    );
  }

  return <InitialsFallback name={name} initials={initials} />;
};

export const constructAvatarInitials = (createdBy: any) => {
  if (typeof createdBy != 'string' || createdBy.length == 0) {
    return '';
  }

  const createdBySplit = createdBy.split(' ');
  const firstName = createdBySplit[0];
  const lastName = createdBySplit.length > 1 ? createdBySplit[1] : undefined;

  return lastName ? `${firstName[0]}${lastName[0]}` : firstName.substring(0, 2);
};

const StyledAvatar = styled(AvatarBase)(() => ({
  width: '100%',
  height: '100%',
}));

// A plain img, not Avatar's src prop: Avatar swaps a failed image for its own person icon and
// never tells the caller, so the initials could not take over.
const AvatarImage = styled('img')(() => ({
  width: '100%',
  height: '100%',
  objectFit: 'cover',
}));

/**
 * Avatar's default text colour is the page background, which is light-on-light against the
 * pale green in the light theme (about 1.1:1). Pick whichever of black or white reads on it.
 */
export const initialsTextColor = (theme: any): string =>
  theme.palette.getContrastText(theme.palette.common.cowbellGreen);

const StyledInitialsAvatar = styled(AvatarBase)(({ theme }) => ({
  backgroundColor: theme.palette.common.cowbellGreen,
  color: initialsTextColor(theme),
  fontSize: '1rem',
  textTransform: 'uppercase',
  width: '100%',
  height: '100%',
}));

export const InitialsAvatar = ({ initials, name }: any) => {
  return <StyledInitialsAvatar alt={name}>{initials}</StyledInitialsAvatar>;
};

/**
 * Profile photo URL for an email, built in the browser (see getAvatarUrl): no request.
 * The URL exists for everyone; a user without a picture gets a 403 and the avatar falls back
 * to initials. Keeps the fields callers read from the former react-query result.
 */
export const useProfilePhoto = ({ email }: any) => ({
  data: getAvatarUrl(email),
  isPreviousData: false,
  isInitialLoading: false,
  isLoading: false,
  isFetching: false,
});
